import { patchOrgAgentsByAgentIdMutation } from '@onefootprint/fetch-dashboard';
import { useRequestErrorToast } from '@onefootprint/hooks';
import { IcoChevronDown16, IcoFileText16, IcoPencil16 } from '@onefootprint/icons';
import { Dropdown } from '@onefootprint/ui';
import { useMutation } from '@tanstack/react-query';
import { cx } from 'class-variance-authority';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import AgentIconPicker from '@/components/agent-icon-picker';
import AGENT_ICONS, { type AgentIconKind, DEFAULT_AGENT_ICON } from '@/components/agent-icon-picker/agent-icons';
import useSession from '@/hooks/use-session';
import { useBuilder } from '../../store/provider';
import EmployeeActions from '../employee-actions';
import PolicyExportDialog from '../export-pdf/policy-export-dialog';
import ModelConfigDialog from '../model-config-dialog/model-config-dialog';
const parseIcon = (icon?: string): AgentIconKind => AGENT_ICONS.includes(icon as AgentIconKind) ? icon as AgentIconKind : DEFAULT_AGENT_ICON;
const AgentNameDropdown = () => {
  const {
    t
  } = useTranslation('agents', {
    keyPrefix: 'agentBuilder.canvas.header'
  });
  const {
    state,
    isReadOnly,
    refresh
  } = useBuilder();
  const {
    data: {
      user
    }
  } = useSession();
  const agentId = state.session?.agentId;
  const [open, setOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [modelConfigOpen, setModelConfigOpen] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [name, setName] = useState(state.bundle.name);
  const [icon, setIcon] = useState<AgentIconKind>(DEFAULT_AGENT_ICON);
  const contentRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const savingRef = useRef(false);
  const showErrorToast = useRequestErrorToast();
  const mutation = useMutation({
    ...patchOrgAgentsByAgentIdMutation(),
    onSuccess: () => refresh({
      silent: true
    }),
    onError: showErrorToast
  });
  useEffect(() => {
    if (renaming) inputRef.current?.select();
  }, [renaming]);
  const closeRename = async (save: boolean) => {
    setOpen(false);
    setRenaming(false);
    const trimmed = name.trim();
    if (!save || isReadOnly || !agentId || !trimmed || savingRef.current) return;
    if (trimmed === state.bundle.name && icon === parseIcon(state.bundle.icon)) return;
    savingRef.current = true;
    try {
      await mutation.mutateAsync({
        path: {
          agentId
        },
        body: {
          name: trimmed,
          icon
        }
      });
    } catch {
      return;
    } finally {
      savingRef.current = false;
    }
  };
  const handleRename = () => {
    setName(state.bundle.name);
    setIcon(parseIcon(state.bundle.icon));
    setRenaming(true);
  };
  const handleExport = () => {
    setOpen(false);
    setExportOpen(true);
  };
  const handleModelConfig = () => {
    setOpen(false);
    setModelConfigOpen(true);
  };
  return <>
      <Dropdown.Root open={open} onOpenChange={nextOpen => {
      if (!nextOpen && renaming) void closeRename(true);else setOpen(nextOpen);
    }}>
        <Dropdown.Trigger asChild>
          <button aria-label={t($ => $.nameMenu.triggerLabel)} type="button" className={cx('flex min-w-0 max-w-full items-center gap-2 rounded-(--button-border-radius) h-7 pl-3 pr-2 py-2 text-label-2 cursor-pointer transition-colors', {
          'bg-primary hover:bg-hover': !open,
          'bg-senary/70': open
        })}>
            <div className="truncate max-w-80">{state.bundle.name}</div>
            <IcoChevronDown16 color="tertiary" className="shrink-0" />
          </button>
        </Dropdown.Trigger>
        <Dropdown.Portal>
          <Dropdown.Content ref={contentRef} align="start" className={renaming ? 'w-100 max-w-[calc(100vw-2rem)]!' : undefined} onEscapeKeyDown={event => {
          if (renaming) {
            event.preventDefault();
            void closeRename(false);
          }
        }}>
            {renaming ? <div className="flex items-center gap-3 p-2">
                <AgentIconPicker value={icon} onChange={setIcon} portalContainerRef={contentRef} />
                <input ref={inputRef} aria-label={t($_0 => $_0.renameDialog.name.label)} value={name} onChange={event_0 => setName(event_0.target.value)} className="min-w-0 flex-1 rounded border border-primary bg-primary px-2 py-2 text-label-2 text-primary focus:outline-brand" onKeyDown={event_1 => {
              event_1.stopPropagation();
              if (event_1.key === 'Enter') {
                event_1.preventDefault();
                void closeRename(true);
              }
              if (event_1.key === 'Escape') {
                event_1.preventDefault();
                void closeRename(false);
              }
            }} />
              </div> : <>
                <Dropdown.Group>
                  <Dropdown.Item iconLeft={IcoPencil16} onSelect={event_2 => {
                event_2.preventDefault();
                handleRename();
              }} disabled={isReadOnly || !agentId || mutation.isPending}>
                    {t($_1 => $_1.nameMenu.rename)}
                  </Dropdown.Item>
                  <Dropdown.Item iconLeft={IcoFileText16} onSelect={handleExport}>
                    {t($_2 => $_2.nameMenu.viewAsPdf)}
                  </Dropdown.Item>
                </Dropdown.Group>
                {user?.isFirmEmployee ? <>
                    <Dropdown.Divider />
                    <EmployeeActions onModelConfig={handleModelConfig} />
                  </> : null}
              </>}
          </Dropdown.Content>
        </Dropdown.Portal>
      </Dropdown.Root>
      <PolicyExportDialog open={exportOpen} onClose={() => setExportOpen(false)} />
      <ModelConfigDialog agentId={agentId || ''} open={modelConfigOpen} onClose={() => setModelConfigOpen(false)} />
    </>;
};
export default AgentNameDropdown;